<script setup lang="ts">
/**
 * Quiz/Scale 3182:15070 — continuous 0–100 rating, five named steps.
 * Idle (`null`): thumb in the middle, nothing recorded, no chip. While dragging the
 * Rate pill follows the thumb; release (or Enter) commits the value.
 */
import { computed, ref } from 'vue'
import FairQuizRatePill from './FairQuizRatePill.vue'
import {
  FAIR_QUIZ_SCALE_INSET,
  FAIR_QUIZ_SCALE_STEPS,
  fairQuizScaleKeyValue,
  fairQuizScaleStep,
  fairQuizScaleValueAt,
} from './fairQuizScale.data'

interface FairQuizScaleProps {
  modelValue: number | null
  /** Five step names, from low to high. */
  stepLabels: readonly string[]
  minLabel: string
  maxLabel: string
  ariaLabel?: string
  disabled?: boolean
}

const props = withDefaults(defineProps<FairQuizScaleProps>(), {
  ariaLabel: undefined,
  disabled: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: number]
  commit: [value: number]
}>()

const root = ref<HTMLElement | null>(null)
const dragging = ref(false)

const isIdle = computed(() => props.modelValue === null)
const value = computed(() => props.modelValue ?? 50)
const step = computed(() => fairQuizScaleStep(value.value))
const stepLabel = computed(() => props.stepLabels[step.value - 1] ?? '')

/** Thumb centre as a CSS length: 20px … (100% − 20px). */
const thumbX = computed(
  () => `calc(${FAIR_QUIZ_SCALE_INSET}px + (100% - ${FAIR_QUIZ_SCALE_INSET * 2}px) * ${value.value / 100})`,
)

function dotX(index: number): string {
  return `calc(${FAIR_QUIZ_SCALE_INSET}px + (100% - ${FAIR_QUIZ_SCALE_INSET * 2}px) * ${index / 4})`
}

/** A dot is white once the fill passes it; the one under the thumb is hidden. */
function dotState(index: number): 'filled' | 'under' | 'empty' {
  if (isIdle.value) return index === 2 ? 'under' : 'empty'
  const at = index * 25
  if (Math.abs(at - value.value) < 6) return 'under'
  return at < value.value ? 'filled' : 'empty'
}

function valueAt(clientX: number): number {
  const rect = root.value?.getBoundingClientRect()
  if (!rect) return value.value
  return fairQuizScaleValueAt(clientX - rect.left, rect.width)
}

function handlePointerDown(event: PointerEvent) {
  if (props.disabled || event.button !== 0) return
  dragging.value = true
  ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
  emit('update:modelValue', valueAt(event.clientX))
}

function handlePointerMove(event: PointerEvent) {
  if (!dragging.value) return
  emit('update:modelValue', valueAt(event.clientX))
}

function handlePointerUp(event: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  const next = valueAt(event.clientX)
  emit('update:modelValue', next)
  emit('commit', next)
}

function handlePointerCancel() {
  dragging.value = false
}

function handleKeydown(event: KeyboardEvent) {
  if (props.disabled) return
  if (event.key === 'Enter') {
    if (isIdle.value) return
    event.preventDefault()
    emit('commit', value.value)
    return
  }
  const next = fairQuizScaleKeyValue(event.key, value.value)
  if (next === null) return
  event.preventDefault()
  emit('update:modelValue', next)
}
</script>

<template>
  <div
    ref="root"
    class="fair-quiz-scale"
    :class="{
      'fair-quiz-scale--idle': isIdle,
      'fair-quiz-scale--dragging': dragging,
      'fair-quiz-scale--high': !isIdle && step >= 3,
      'fair-quiz-scale--warm': !isIdle && step >= 4,
      'fair-quiz-scale--disabled': disabled,
    }"
    :style="{ '--fair-quiz-scale-x': thumbX }"
    @pointerdown="handlePointerDown"
    @pointermove="handlePointerMove"
    @pointerup="handlePointerUp"
    @pointercancel="handlePointerCancel"
  >
    <span class="fair-quiz-scale__track" />
    <span
      v-if="!isIdle"
      class="fair-quiz-scale__fill"
    />
    <span
      v-for="(_, index) in FAIR_QUIZ_SCALE_STEPS"
      :key="index"
      class="fair-quiz-scale__step"
      :class="`fair-quiz-scale__step--${dotState(index)}`"
      :style="{ left: dotX(index) }"
    />
    <span
      v-if="!isIdle"
      class="fair-quiz-scale__halo"
    />
    <span
      class="fair-quiz-scale__thumb"
      role="slider"
      :tabindex="disabled ? -1 : 0"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="isIdle ? undefined : value"
      :aria-valuetext="isIdle ? undefined : stepLabel"
      :aria-label="ariaLabel"
      :aria-disabled="disabled || undefined"
      @keydown="handleKeydown"
    >
      <span class="fair-quiz-scale__dot" />
    </span>
    <FairQuizRatePill
      v-if="!isIdle"
      class="fair-quiz-scale__pill"
      :step="step"
      :label="stepLabel"
    />
    <span class="fair-quiz-scale__label fair-quiz-scale__label--min">{{ minLabel }}</span>
    <span class="fair-quiz-scale__label fair-quiz-scale__label--max">{{ maxLabel }}</span>
  </div>
</template>
